import apiClient from './meta-client';
import {
    MetaApp,
    CreateMetaAppInput,
    UpdateMetaAppInput,
    MetaUser,
    MetaPage,
    OAuthInitResponse,
    SyncPagesRequest,
    SyncPagesResponse,
    PageSyncPreviewRequest,
    PageSyncPreviewResponse,
    PageSyncCommitRequest,
    UpdatePageConfigInput,
    PageCategory,
    PageVariable,
    PageVariableInput,
} from '@/pages/Meta/Connection/types/meta.types';

// ====================================
// META APPS
// ====================================

export const getMetaApps = async (): Promise<MetaApp[]> => {
    const response = await apiClient.get('/meta/apps');
    return response.data;
};

export const getMetaApp = async (id: number): Promise<MetaApp> => {
    const response = await apiClient.get(`/meta/apps/${id}`);
    return response.data;
};

export const getDefaultMetaApp = async (): Promise<MetaApp> => {
    const response = await apiClient.get('/meta/apps/default');
    return response.data;
};

export const createMetaApp = async (data: CreateMetaAppInput): Promise<MetaApp> => {
    const response = await apiClient.post('/meta/apps', data);
    return response.data;
};

export const updateMetaApp = async (id: number, data: UpdateMetaAppInput): Promise<MetaApp> => {
    const response = await apiClient.put(`/meta/apps/${id}`, data);
    return response.data;
};

export const updateMetaAppStatus = async (id: number, is_active: boolean): Promise<{ message: string }> => {
    const response = await apiClient.patch(`/meta/apps/${id}/status`, { is_active });
    return response.data;
};

export const deleteMetaApp = async (id: number): Promise<{ message: string }> => {
    const response = await apiClient.delete(`/meta/apps/${id}`);
    return response.data;
};

// ====================================
// META OAUTH
// ====================================

export const initMetaOAuth = async (meta_app_id: number | null = null): Promise<OAuthInitResponse> => {
    const response = await apiClient.post('/meta/oauth/init', { meta_app_id });
    return response.data;
};

export const processOAuthCallback = async (code: string, state?: string): Promise<any> => {
    const params = new URLSearchParams({ code });
    if (state) params.append('state', state);
    const response = await apiClient.get(`/meta/oauth/callback?${params.toString()}`);
    return response.data;
};

// ====================================
// META USERS (Authentications)
// ====================================

export const getMetaUsers = async (): Promise<MetaUser[]> => {
    const response = await apiClient.get('/meta/users');
    return response.data;
};

export const revokeMetaUser = async (authId: number): Promise<{ message: string }> => {
    const response = await apiClient.delete(`/meta/users/${authId}`);
    return response.data;
};

/** Atribui projeto default à conta Meta. project_id=null desvincula. */
export const updateMetaUserProject = async (
    authId: number,
    project_id: number | null,
): Promise<{ message: string }> => {
    const response = await apiClient.patch(`/meta/users/${authId}/project`, { project_id });
    return response.data;
};

// ====================================
// META PAGES
// ====================================

export const getMetaPages = async (): Promise<MetaPage[]> => {
    const response = await apiClient.get('/meta/pages', { params: { all: true } });
    return Array.isArray(response.data) ? response.data : (response.data?.items ?? []);
};

export interface PaginatedPages {
    items: MetaPage[];
    total: number;
    page: number;
    per_page: number;
    pages: number;
}

export const getMetaPagesPaginated = async (
    page: number = 1,
    per_page: number = 20,
    search?: string,
): Promise<PaginatedPages> => {
    const params: Record<string, any> = { page, per_page };
    if (search) params.search = search;
    const response = await apiClient.get('/meta/pages', { params });
    return response.data;
};

export const getActiveMetaPages = async (): Promise<MetaPage[]> => {
    const response = await apiClient.get('/meta/pages/active');
    return response.data;
};

export const syncMetaPages = async (data: SyncPagesRequest): Promise<SyncPagesResponse> => {
    const response = await apiClient.post('/meta/pages/sync', data);
    return response.data;
};

/** Pré-visualiza as páginas de uma conta (sem gravar) pro picker de seleção. */
export const previewSyncMetaPages = async (
    data: PageSyncPreviewRequest,
): Promise<PageSyncPreviewResponse> => {
    const response = await apiClient.post('/meta/pages/sync/preview', data);
    return response.data;
};

/** Sincroniza só as páginas escolhidas no picker (desativa as demais). */
export const commitSyncMetaPages = async (
    data: PageSyncCommitRequest,
): Promise<SyncPagesResponse> => {
    const response = await apiClient.post('/meta/pages/sync/commit', data);
    return response.data;
};

export const updateMetaPageStatus = async (id: number, is_active: boolean): Promise<{ message: string }> => {
    const response = await apiClient.patch(`/meta/pages/${id}/status`, { is_active });
    return response.data;
};

/** Override do projeto pra uma página específica. project_id=null = herda da conta. */
export const updateMetaPageProject = async (
    id: number,
    project_id: number | null,
): Promise<{ message: string }> => {
    const response = await apiClient.patch(`/meta/pages/${id}/project`, { project_id });
    return response.data;
};

export const updateMetaPageConfig = async (id: number, data: UpdatePageConfigInput): Promise<{ message: string }> => {
    const response = await apiClient.patch(`/meta/pages/${id}/config`, data);
    return response.data;
};

export interface BulkPageConfigPayload {
    page_ids: number[];
    /** Categoria interna (chatmeta_page_categories.id). Omitir pra não alterar. */
    internal_category_id?: number;
    /** Idioma (ex: 'pt_BR'). Omitir pra não alterar. */
    language?: string;
    /** Fuso (ex: 'America/Sao_Paulo'). Omitir pra não alterar. */
    timezone?: string;
}

export interface BulkPageConfigResponse {
    updated: number;
    skipped: number[];
}

/**
 * Atualiza timezone/language/internal_category_id em até 500 páginas de uma vez.
 * Campos não preenchidos no payload ficam intocados.
 * Páginas fora do escopo do user retornam em `skipped` (não erra).
 */
export const bulkUpdateMetaPagesConfig = async (
    payload: BulkPageConfigPayload
): Promise<BulkPageConfigResponse> => {
    const response = await apiClient.patch('/meta/pages/bulk-config', payload);
    return response.data;
};

export const deleteMetaPage = async (id: number): Promise<{ message: string }> => {
    const response = await apiClient.delete(`/meta/pages/${id}`);
    return response.data;
};

// ====================================
// PAGE CATEGORIES
// ====================================

export const getPageCategories = async (): Promise<PageCategory[]> => {
    const response = await apiClient.get('/meta/page-categories');
    return response.data;
};

// ====================================
// PAGE VARIABLES
// ====================================

export const getPageVariables = async (pageId: number): Promise<PageVariable[]> => {
    const response = await apiClient.get(`/meta/pages/${pageId}/variables`);
    return response.data;
};

export const getPageVariablesBatch = async (pageIds: number[]): Promise<Record<string, PageVariable[]>> => {
    const response = await apiClient.post('/meta/pages/variables-batch', pageIds);
    return response.data;
};

export const createPageVariable = async (pageId: number, data: PageVariableInput): Promise<PageVariable> => {
    const response = await apiClient.post(`/meta/pages/${pageId}/variables`, data);
    return response.data;
};

export const updatePageVariable = async (pageId: number, variableId: number, data: Partial<PageVariableInput>): Promise<PageVariable> => {
    const response = await apiClient.patch(`/meta/pages/${pageId}/variables/${variableId}`, data);
    return response.data;
};

export const deletePageVariable = async (pageId: number, variableId: number): Promise<{ message: string }> => {
    const response = await apiClient.delete(`/meta/pages/${pageId}/variables/${variableId}`);
    return response.data;
};

export const bulkUpsertPageVariables = async (pageId: number, variables: PageVariableInput[]): Promise<PageVariable[]> => {
    const response = await apiClient.put(`/meta/pages/${pageId}/variables`, variables);
    return response.data;
};

export const createPageCategory = async (data: { name: string }): Promise<PageCategory> => {
    const response = await apiClient.post('/meta/page-categories', data);
    return response.data;
};

export const updatePageCategory = async (id: number, data: { name: string }): Promise<PageCategory> => {
    const response = await apiClient.patch(`/meta/page-categories/${id}`, data);
    return response.data;
};

export const deletePageCategory = async (id: number): Promise<{ message: string }> => {
    const response = await apiClient.delete(`/meta/page-categories/${id}`);
    return response.data;
};

// ====================================
// Page App Capabilities
// ====================================

import type { PageTokenCapability } from '@/pages/Meta/Connection/types/meta.types';

export interface PageAppCapabilityUpdate {
    app_id_pk: number;
    capabilities: PageTokenCapability[] | null;  // null = legacy/todas
}

/** Atualiza capabilities de cada vínculo (página, app). */
export const updatePageAppCapabilities = async (
    pageId: number,
    updates: PageAppCapabilityUpdate[],
): Promise<{ message: string; updated: number }> => {
    const response = await apiClient.put(`/meta/pages/${pageId}/app-capabilities`, { updates });
    return response.data;
};
